import { afterNextRender, afterRenderEffect, ChangeDetectionStrategy, Component, computed, DestroyRef, EventEmitter, inject, signal, TemplateRef, untracked, viewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { firstValueFrom, from, throwError } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { ICheckup, IHandleResponse, IProfile, ProfileService } from '../profile.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { calculatePriceWithCoupon, ICouponUse, IPackage } from '../../../types';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { PaintDirective } from '../../../paint.directive';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { MatError, MatHint } from '@angular/material/form-field';
import { ConfirmPopupAction, ConfirmPopupComponent } from '../../../confirm-popup/confirm-popup.component';
import { ConfirmPopup } from '../../../confirm.popup';
import { CardAddedEvent, CardInputComponent, CardService } from "../../../card-input/card-input.component";

const accountStates = ['accountValid', 'accountMissingFields', 'accountNotVerified', 'accountNotOptIn'] as const;
const premiumStates = ['premiumFree', 'premiumFreePending', 'premiumPremium', 'premiumPremiumProvided', 'premiumPremiumPending', 'premiumPremiumPendingTruevo'] as const;

interface AccountStateVariable {
  templateName: typeof accountStates[number];
  icon: string;
  paint: string;
  metadata?: { [key: string]: any };
}

interface PremiumStateVariable {
  templateName: typeof premiumStates[number];
  icon: string;
  paint: string;
  metadata?: { [key: string]: any };
}

type CardEvent<T> = T extends "add" ? CardAddedEvent : string;

const warningFields = ['optin_email', 'optin_email_remarketing', 'optin_sms', 'optin_sms_remarketing', 'optin_push', 'optin_push_remarketing'] as const;
const importantFields = ['email', 'birthday'] as const;

@Component({
  selector: 'profile-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatButtonModule, MatExpansionModule, PaintDirective, MatDividerModule, MatListModule, RouterLink, NgTemplateOutlet, DatePipe, ReactiveFormsModule, MatHint, MatError, CardInputComponent]
})
export class ProfileAccountComponent {
  service = inject(ProfileService);
  activated = inject(ActivatedRoute);
  destroy = inject(DestroyRef);
  card = inject(CardService);
  dialogs = inject(MatDialog);
  confirm = inject(ConfirmPopup);

  cardTypes: string[] = [
    'amex',
    'diners',
    'discover',
    'elo',
    'jcb',
    'mastercard',
    'unionpay',
    'visa'
  ];

  reload = new EventEmitter<boolean>();

  state = signal<ICheckup|null>(null);
  isLoading = signal<boolean>(true);

  account_state = computed(() => {

    const consumer = this.state()?.consumer;

    let variables: AccountStateVariable = { templateName: 'accountValid', icon: 'check', paint: 'success' };

    if(consumer){
      if(importantFields.some(field => !consumer[field])){
        variables = { templateName: 'accountMissingFields', icon: 'warning', paint: 'warn' };
      } else if(!consumer.email_verified){
        variables = { templateName: 'accountNotVerified', icon: 'warning', paint: 'warn' };
      } else if(warningFields.some(field => !consumer[field])){
        variables = { templateName: 'accountNotOptIn', icon: 'warning', paint: 'accent' };
      }
    }

    return variables;
  });

  account_template = computed(() => {
    const account_state = this.account_state();
    return this[account_state.templateName]()!;
  });

  premium_state = computed(() => {

    const { membership, transaction, use } = this.state() || {};

    let variables: PremiumStateVariable = { templateName: 'premiumFree', icon: 'warning', paint: 'accent' };

    if(membership){
      variables = { templateName: 'premiumPremium', icon: 'check', paint: 'success' };

      if(membership.package?._id != this.service.auth.userPackage()){
        this.service.auth.sync();
      }

      if(membership.package?.package_type == 'corporate'){
        variables.templateName = 'premiumPremiumProvided';
      } else if(transaction && ['ACTION_REQUIRED', 'FAILED'].includes(transaction.status)){
        variables = { templateName: transaction.truevo ? 'premiumPremiumPendingTruevo' : 'premiumPremiumPending', icon: 'warning', paint: 'warn' };
      } else if(membership.package?.recurs && !membership.recurs){
        variables.icon = 'warning';
        variables.paint = 'accent';
      }

      variables.metadata = {
        current_price: transaction?.final_price ?? membership.package.price ?? '49.99',
        next_price: membership.package?.price ?? 'Unknown'
      };

      if(use){
        variables.metadata.next_price = calculatePriceWithCoupon(membership.package, use.coupon.percentage, use.coupon.reduction);
      }

    } else if(transaction?.status == 'ACTION_REQUIRED') {
      variables = { templateName: 'premiumFreePending', icon: 'warning', paint: 'accent' }
    }

    return variables;
  });

  premium_template = computed(() => {
    const premium_state = this.premium_state();
    return this[premium_state.templateName]()!;
  });

  id: string|null = null;
  validId: RegExp = new RegExp(/^[0-9a-f]{24}$/);
  
  accountValid = viewChild<TemplateRef<any>>('accountValid');
  accountNotVerified = viewChild<TemplateRef<any>>('accountNotVerified');
  accountNotOptIn = viewChild<TemplateRef<any>>('accountNotOptIn');
  accountMissingFields = viewChild<TemplateRef<any>>('accountMissingFields');
  
  currentDialog: MatDialogRef<any>|null = null;
  
  premiumFree = viewChild<TemplateRef<any>>('premiumFree');
  premiumFreePending = viewChild<TemplateRef<any>>('premiumFreePending');
  premiumPremium = viewChild<TemplateRef<any>>('premiumPremium');
  premiumPremiumProvided = viewChild<TemplateRef<any>>('premiumPremiumProvided');
  premiumPremiumPending = viewChild<TemplateRef<any>>('premiumPremiumPending');
  premiumPremiumPendingTruevo = viewChild<TemplateRef<any>>('premiumPremiumPendingTruevo');

  constructor() {

    this.id = this.activated.snapshot.queryParams.id;

    afterNextRender(() => {
      this.reload.pipe(
        takeUntilDestroyed(this.destroy), 
        tap(() => this.isLoading.set(true)), 
        switchMap(state => this.service.getStats())
      ).subscribe({
        next: response => {
          this.state.set(response);
          this.isLoading.set(false);
          // if(this.activated.snapshot.queryParams.code){
          //   this.promptEnterCode(false, this.activated.snapshot.queryParams.code);
          // }
        },
        error: error => {
          this.isLoading.set(false);
        }
      });

      if(this.id){
        this.retryPayment(this.id);
      }
    })

    afterRenderEffect(() => {
      const session = this.service.auth.userSession();
      // console.log('SESSION', session);
      untracked(() => {
        this.reload.emit(false);
      });
    });

   }

   promptAddPaymentMethod(title: string, content: TemplateRef<any>|string, label: string, id: string){
    return this.confirm.open(title, content, label, [
      {
        label: 'Add Payment Method',
        paint: 'primary',
        close: true,
        callback: () => {
          this.promptCardSelector(id);
        }
      }
    ]);
   }

   promptInsufficentFundsRetry(id: string){
    return this.confirm.open('Try again?', `Your last charge failed due to insufficient funds. Have you added enough funds?`, 'Last charge failed', [
      {
        label: 'No',
        paint: 'warn',
        close: true
      },
      {
        label: 'Retry',
        paint: 'primary',
        close: true,
        callback: (ref) => {
          this.retryPayment(id, true);
        }
      }
    ]);
   }

   promptMembershipToggle(){
    const { membership } = this.state() || {};
    if(membership){
      return this.confirm.open('Continue?', `Are you sure you would like to ${membership.recurs ? 'cancel your subscription' : 'resubscribe'}?`, 'Confirm membership update', [
        {
          label: 'Cancel',
          paint: 'warn',
          close: true
        },
        {
          label: membership.recurs ? 'Cancel' : 'Renew',
          paint: 'primary',
          close: true,
          callback: (ref) => {
            this.isLoading.set(true);
            this.service.toggleSubscription(membership._id, !membership.recurs).pipe().subscribe({
              next: response => {
                this.service.toast.success(response.message);
                this.state.update(state => {
                  if(state){
                    
                    if(state.membership){
                      state.membership.recurs = !state.membership.recurs;
                      state.membership = Object.assign({}, state.membership);
                    }
  
                    return Object.assign({}, state);
                  } else {
                    return state;
                  }
                });
                this.isLoading.set(false);
              },
              error: error => {
                this.service.toast.error('There was a problem with your request. Please try again and contact us if it persists.');
                this.isLoading.set(false);
              }
            });
          }
        }
      ]);
    }
    return null;
   }

   promptRetryAfterCardUpdate(title: string, content: TemplateRef<any>|string, label: string, id?: string){
    return this.confirm.open(title, content, label, [
      {
        label: 'No',
        close: true,
        paint: 'warn'
      },
      {
        label: 'Yes',
        close: true,
        paint: 'primary',
        callback: () => this.retryPayment(id)
      }
    ]);
   }

   promptCheckout(pkg: IPackage, use: ICouponUse){
    const { _id: package_id, slug } = pkg;
    const { _id: use_id, code, coupon: { bypass } } = use;

    let button: ConfirmPopupAction = bypass ? {
      label: 'Activate Now',
      paint: 'primary',
      close: true,
      callback: (ref) => this.checkoutPayment(package_id, use_id) 
    } : {
      label: 'Go to Checkout',
      route: `/checkout/${slug}?code=${code}`,
      paint: 'primary',
      close: true
    };

    return this.confirm.open('Code has been added', 'Would you like to use your code now?', 'Activate code prompt', [
      {
        label: 'Later',
        close: true,
        paint: 'warn'
      },
      button
    ]);
   }

  verifyEmail(){
    this.service.authEmailResend().pipe(takeUntilDestroyed(this.destroy)).subscribe({
      next: ({ message }) => {
        this.service.toast.info(`An email has been sent for verification`);
      },
      error: error => {
        this.service.toast.error(error.message);
      }
    });
  }

  handleResponse(result: IHandleResponse){
    switch(result.status){
      case 'COMPLETED': {
        this.service.auth.sync();
        if(this.id){
          this.service.router.navigate(['/post-checkout'], { queryParams: { 
            ...result.package ? { p: result.package } : {} 
          }});
        } else {
          this.service.toast.success('Your transaction was successful and your account status will be updated.');
        }
        // this.message = 'Transaction was successful';
        break;
      }
      case 'ACTION_REQUIRED': {

        if(!result.client_secret || !['requires_payment_method', 'requires_confirmation', 'requires_action'].includes(result.intent_status || '')){
          this.service.toast.error('Transaction reached an invalid state. Please contact us on support@cloudigo.app or on Messenger.');
          break;
        }

        from(this.card.stripe.confirmCardPayment(result.client_secret, result.payment_method ? {
            payment_method: result.payment_method
          } : {}
        )).pipe(
          take(1),
          takeUntilDestroyed(this.destroy),
          switchMap(({paymentIntent, error}) => error ? throwError(() => error) : this.service.handleAction(result._id)),
        ).subscribe({
          next: response => {
            this.handleResponse(response);
          },
          error: error => {
            const badCardCodes = [
              'incorrect_number',
              'incorrect_cvc',
              'incorrect_zip',
              'invalid_cvc',
              'invalid_number',
              'invalid_expiry_month',
              'invalid_expiry_year',
              'expired_card',
            ];
            if(error && error.code && (badCardCodes.includes(error.decline_code || '') || badCardCodes.includes(error.code || ''))){

              this.promptAddPaymentMethod('Transaction error', `Transaction failed with the following error: ${error.message}<br/>To solve this issue, please update your payment method.`, 'Transaction failed prompt', result._id);

            } else {
              this.service.toast.error(error?.message ? `Transaction failed with the following error: ${error.message}` : 'Authorization failed due to an unknown error. If problem persists, contact us on support@cloudigo.app or on Messenger.');
            }
            this.isLoading.set(false);
          }
        });

        break;
      }
      default: {
        this.reload.emit(true);
        this.service.toast.error(result.error || 'Transaction failed due to an unknown error. If problem persists, contact us on support@cloudigo.app or on Messenger.');
        // this.message = 'Transaction failed. ' + result.error;
        this.isLoading.set(false);
        break;
      } 
    }

  }

  retryPayment(_id: string|null = null, bypass: boolean = false) {
    const id = _id || this.state()?.transaction?._id || null;
    if(!id) return;
    this.isLoading.set(true);

    if(!bypass && (!_id || _id == id)){
      const last_error = this.state()?.transaction?.resultcode || null;

      if(last_error == 'insufficient_funds'){
        this.promptInsufficentFundsRetry(id);
        return;
      }

      //if we're using the transaction we already have
    }

    this.service.handleRetry(id).subscribe({
      next: response => this.handleResponse(response),
      error: error => {
        if(error.code && error.code == 31){
          //No payment methods error
          this.promptAddPaymentMethod('Transaction error', `Transaction failed with the following error: <br/><br/>${error.message}<br/>To solve this issue, please update your payment method.`, 'Transaction failed prompt', id);

        } else if(error.code){
          //No payment methods error
          this.service.toast.error(error.message);
          this.reload.emit(true);
        } else {
          console.error('retry-failed', error);
          this.service.toast.error('Retry failed due to an unknown error. If problem persists, contact us on support@cloudigo.app or on Messenger.');
        }
        this.isLoading.set(false);
      }
    });

    return true;
  }

  checkoutPayment(package_id: string, use_id: string) {
    this.isLoading.set(true);
    this.service.handleCheckout(package_id, use_id, undefined, undefined, false, this.service.router.isFromApp() ? 'App' : 'Web').subscribe({
      next: response => this.handleResponse(response),
      error: error => {
        console.error('checkout-failed', error);
        this.service.toast.error('Transaction failed due to an unknown error. If problem persists, contact us on support@cloudigo.app or on Messenger.');
        this.isLoading.set(false);
      }
    });

    return true;
  }

  card_selector = viewChild<TemplateRef<any>>('cardSelector');

  promptCardSelector(retry_transaction_id?: string) {
    const card_selector = this.card_selector();

    if(card_selector){
      this.confirm.open('Select a Card', card_selector, 'Card Selector', [], 'Close').afterClosed().pipe(takeUntilDestroyed(this.destroy)).subscribe(label => {
        if(label && label != 'Close'){
          this.reload.emit();
          if(retry_transaction_id && !label.includes('Retried')){
            const title = label == 'Add' ? 'Card Added' : 'Card Updated';
            this.promptRetryAfterCardUpdate(title, `${label == 'Add' ? 'Your card has been added' : 'Your card has been updated'}. Would you like to retry payment now?`, title + ' Prompt', retry_transaction_id);
          }
        }
      });
    }
  }

  code: FormControl = new FormControl<string>('', { nonNullable: true });
  code_prompt = viewChild<TemplateRef<any>>('codePrompt');
  promptEnterCode(fromPending: boolean = false, code: string = '') {
    this.code.reset(code);
    const code_prompt = this.code_prompt();
    if(code_prompt){
      this.confirm.open('Enter Code', code_prompt, 'Code Input', [
        {
          label: 'Submit',
          paint: 'primary',
          callback: (ref) => {
            this.submitCouponInput(fromPending, ref);
          }
        }
      ], 'Close', null, { from_pending: fromPending });
    }

    return true;
  }

  submitCouponInput(from_pending: boolean, dialog_ref: MatDialogRef<ConfirmPopupComponent>) {
    if(this.isLoading() || this.code.invalid){
      return;
    }

    this.isLoading.set(true);

    this.service.addCoupon(this.code.value).pipe(takeUntilDestroyed(this.destroy)).subscribe({
      next: use => {
        if(use){
          dialog_ref?.close();

          const { package: pkg } = use.coupon;
          const { transaction } = this.state() || {};

          if(pkg){
            if(this.service.auth.userPackage() == pkg._id && from_pending && transaction){

              this.promptRetryAfterCardUpdate('Code has been added', 'Would you like to retry payment now?', 'Card Added');

            } else {
              this.promptCheckout(pkg, use);
            }
          }
        }
        this.isLoading.set(false);
      },
      error: error => {
        this.isLoading.set(false);
        this.code.setErrors({
          coupon: error.message
        });
      }
    });

    return true;
  }

  onCardAction<T extends "add" | "update" | "prefer" | "delete">(event: T, dialog_ref: MatDialogRef<ConfirmPopupComponent>, originalEvent: CardEvent<T>){
    // console.log(event);
    switch(event){
      case 'add':
        //event: { _id, should_sync}
        if(typeof originalEvent != 'string' && originalEvent.should_sync){
          this.service.auth.sync();
          this.service.toast.success('Card has been added and your pending transaction has been completed!');
          dialog_ref?.close('Add_Retried');
        } else {
          this.service.toast.success('Card has been added');
          dialog_ref?.close('Add');
        }
      break;
      case 'update':
      case 'prefer':
        this.service.toast.success(`Card has been ${event == 'update' ? 'updated' : 'prefered'}.`);
        dialog_ref?.close('Update');
      break;
      case 'delete':
        this.service.toast.success('Card has been removed');
      break;
    }
  }
}
